<!-- Показывает список блоков шаблона и действия управления структурой -->
<template>
  <div class="blocks-wrapper" :class="{ 'is-readonly': readOnly }">
    <div v-if="!readOnly" class="header-row">
      <h3 class="section-title">Структура шаблона</h3>
    </div>

    <div
      v-if="!readOnly && isStylesAllowed"
      class="global-styles-section"
    >
      <TemplateStyleEditor
        ref="globalStyleEditor"
        v-model="localGlobalStyles"
        :is-global-mode="true"
        @save="handleSaveGlobalStyles"
        @cancel="handleCancelGlobalStyles"
        @reset="handleResetGlobalStyles"
      />
    </div>

    <base-loader v-if="isLocalLoading" />

    <div v-else>
      <div class="blocks-list-content">
        <draggable
          v-if="localBlocks.length > 0"
          v-model="localBlocks"
          item-key="id"
          tag="div"
          class="draggable-area"
          handle=".drag-handle"
          :disabled="readOnly"
          @end="onDragEnd"
        >
          <template #item="{ element: block }">
            <div class="block-wrapper">
              <!-- Режим редактирования -->
              <div
                v-if="!readOnly && editingBlockId === block.id"
                class="edit-block-mode"
              >
                <TemplateBlockForm
                  :initial-data="block"
                  :template-id="templateId"
                  :company-id="companyId"
                  :record-id="recordId"
                  :record-type="recordType"
                  :template-global-styles="templateGlobalStyles"
                  @save="stopEditing"
                  @cancel="stopEditing"
                />
              </div>

              <!-- Режим Просмотра -->
              <TemplateBlockItem
                v-else
                :block="block"
                :read-only="readOnly"
                @edit="startEditing(block.id)"
                @remove="handleDisableBlock(block)"
              />
            </div>
          </template>
        </draggable>

        <div v-else-if="!isCreating" class="no-data">
          В этом шаблоне нет активных блоков
        </div>

        <!-- Форма создания -->
        <div
          v-if="isCreating && !readOnly"
          class="create-form-wrapper"
        >
          <TemplateBlockForm
            :template-id="templateId"
            :company-id="companyId"
            :is-create-mode="true"
            :next-position="localBlocks.length + 1"
            :template-global-styles="templateGlobalStyles"
            @save="stopCreating"
            @cancel="stopCreating"
          />
        </div>

        <div v-if="!isCreating && !readOnly" class="add-btn-wrapper">
          <base-button @click.stop="startCreating" class="btn-add">
            Добавить блок
          </base-button>
        </div>
      </div>
    </div>

    <BaseDialog
      :visible="Boolean(blockPendingDisable)"
      title="Отключить блок?"
      message="Блок будет скрыт из структуры шаблона"
      :actions="[
        { key: 'confirm', label: 'Отключить', type: 'danger' },
        { key: 'close', label: 'Отмена', type: 'secondary' },
      ]"
      @action="handleDisableBlockDialogAction"
    />
  </div>
</template>

<script>
import { mapActions, mapState } from "pinia";
import { useTemplateStore } from "@/stores/templateStore";
import { useNotificationStore } from "@/stores/notificationStore";

import draggable from "vuedraggable";

import TemplateBlockItem from "./TemplateBlockItem.vue";
import TemplateBlockForm from "./TemplateBlockForm.vue";
import TemplateStyleEditor from "./TemplateStyleEditor.vue";

export default {
  name: "TemplateBlocksList",
  components: {
    draggable,
    TemplateBlockItem,
    TemplateBlockForm,
    TemplateStyleEditor,
  },
  props: {
    templateId: { type: [Number, String], required: true },
    companyId: { type: [Number, String], required: true },
    readOnly: { type: Boolean, default: false },
  },
  data() {
    return {
      recordId: null,
      recordType: "Order",
      localBlocks: [],
      isLocalLoading: false,
      isReordering: false,

      editingBlockId: null,
      isCreating: false,

      templateGlobalStyles: {},
      localGlobalStyles: {},
      currentTemplateConfig: null,
      currentTemplate: null,
      blockPendingDisable: null,
    };
  },
  computed: {
    ...mapState(useTemplateStore, ["templateBlocks"]),

    notificationStore() {
      return useNotificationStore();
    },

    storeBlocks() {
      const blocks = this.templateBlocks[this.templateId];

      if (Array.isArray(blocks)) {
        return blocks.filter(
          (b) =>
            b.template_id == this.templateId && b.enabled !== false
        );
      }
      return [];
    },

    isStylesAllowed() {
      return this.currentTemplate?.kind !== "order_invoice";
    },
  },
  watch: {
    storeBlocks: {
      handler(newVal) {
        return (this.localBlocks = [...newVal]);
      },
      immediate: true,
      deep: true,
    },
  },
  methods: {
    ...mapActions(useTemplateStore, [
      "getTemplateBlocksRequest",
      "reorderBlocks",
      "blockDisable",
      "loadPreviewRecord",
      "showTemplate",
      "updateTemplate",
    ]),

    parseConfig(config) {
      if (!config) return {};

      if (typeof config === "string") {
        try {
          return JSON.parse(config);
        } catch (e) {
          console.error("Ошибка парсинга config:", e);
          return {};
        }
      }

      return config;
    },

    async loadTemplateData() {
      try {
        const template = await this.showTemplate(this.templateId);

        this.currentTemplate = { ...template };

        const parsedConfig = this.parseConfig(template.config);
        this.currentTemplateConfig = parsedConfig;

        if (parsedConfig && parsedConfig.style) {
          this.templateGlobalStyles = { ...parsedConfig.style };
          this.localGlobalStyles = { ...parsedConfig.style };
        } else {
          this.templateGlobalStyles = {};
          this.localGlobalStyles = {};
        }
      } catch (error) {
        console.error("Ошибка загрузки данных шаблона:", error);
      }
    },

    async handleSaveGlobalStyles(styles) {
      try {
        const updatedConfig = {
          ...this.currentTemplateConfig,
          style: styles,
        };

        const payload = {
          name: this.currentTemplate.name,
          description: this.currentTemplate.description,
          enabled: this.currentTemplate.enabled,
          config: updatedConfig,
        };

        await this.updateTemplate(this.templateId, payload);

        this.currentTemplateConfig = updatedConfig;
        this.templateGlobalStyles = styles || {};
        this.localGlobalStyles = styles || {};

        this.$refs.globalStyleEditor?.updateOriginalStyles(styles);

        this.notificationStore.showSuccess(
          "Глобальные стили сохранены"
        );

        this.$refs.globalStyleEditor?.resetSaving();

        await this.loadTemplateData();
      } catch (error) {
        console.error(
          "Ошибка при сохранении глобальных стилей:",
          error
        );
        this.notificationStore.showError("Ошибка сохранения стилей");
        this.$refs.globalStyleEditor?.resetSaving();
      }
    },

    handleCancelGlobalStyles() {
      this.notificationStore.showInfo("Изменения отменены");
    },

    async handleResetGlobalStyles() {
      try {
        const updatedConfig = { ...this.currentTemplateConfig };
        delete updatedConfig.style;

        const payload = {
          name: this.currentTemplate.name,
          description: this.currentTemplate.description,
          enabled: this.currentTemplate.enabled,
          config:
            Object.keys(updatedConfig).length > 0
              ? updatedConfig
              : null,
        };

        await this.updateTemplate(this.templateId, payload);

        this.currentTemplateConfig = updatedConfig;
        this.templateGlobalStyles = {};
        this.localGlobalStyles = {};

        this.$refs.globalStyleEditor?.updateOriginalStyles({});

        this.notificationStore.showSuccess(
          "Глобальные стили сброшены"
        );

        this.$refs.globalStyleEditor?.resetSaving();

        await this.loadTemplateData();
      } catch (error) {
        console.error("Ошибка при сбросе глобальных стилей:", error);
        this.notificationStore.showError("Ошибка сброса стилей");
        this.$refs.globalStyleEditor?.resetSaving();
      }
    },

    startEditing(id) {
      if (this.readOnly) return;
      this.isCreating = false;
      this.editingBlockId = id;
    },

    stopEditing() {
      this.editingBlockId = null;
    },

    startCreating() {
      if (this.readOnly) return;
      this.editingBlockId = null;
      this.isCreating = true;
    },

    stopCreating() {
      this.isCreating = false;
    },

    handleDisableBlock(block) {
      this.blockPendingDisable = block;
    },

    async handleDisableBlockDialogAction(action) {
      if (action !== "confirm") {
        this.blockPendingDisable = null;
        return;
      }

      const block = this.blockPendingDisable;
      this.blockPendingDisable = null;
      if (!block) return;

      try {
        await this.blockDisable(this.templateId, block.id);

        this.notificationStore.showSuccess("Блок успешно отключен");
      } catch (error) {
        console.error("Ошибка при отключении блока:", error);
      }
    },

    async onDragEnd() {
      if (this.isReordering || this.readOnly) return;
      this.isReordering = true;
      try {
        const reorderData = {
          points: this.localBlocks.map((b, index) => ({
            id: b.id,
            sequence_number: index + 1,
          })),
        };

        await this.reorderBlocks(this.templateId, reorderData);

        this.notificationStore.showSuccess(
          "Порядок блоков успешно изменен"
        );
      } finally {
        this.isReordering = false;
      }
    },
  },

  async created() {
    this.isLocalLoading = true;
    await this.loadTemplateData();
    await this.getTemplateBlocksRequest(this.templateId);
    this.recordId = await this.loadPreviewRecord({
      companyId: this.companyId,
    });
    this.isLocalLoading = false;
  },
};
</script>

<style scoped>
.section-title {
  margin-bottom: var(--base-in-indent);
}

.global-styles-section {
  margin-bottom: var(--base-in-indent);
}

.draggable-area {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.is-readonly .draggable-area {
  gap: 8px;
}

.edit-block-mode {
  margin: 5px 0;
}

.create-form-wrapper,
.add-btn-wrapper {
  margin-top: 15px;
}

.btn-add {
  background-color: #4f46e5 !important;
  color: #fff !important;
  font-size: 14px;
}

.no-data {
  margin-bottom: var(--base-in-indent);
}
</style>
